import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "../lib/api";
import { fmt } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { BrandLogo } from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Building2, Users, TrendingUp, ShoppingBag, Plus, LogOut, ExternalLink, Copy, PauseCircle, PlayCircle, UserCog, MapPin, Check, X, Ban } from "lucide-react";

const STATUS_COLORS = { active: "bg-[hsl(var(--success))] text-white", trialing: "bg-blue-500 text-white", suspended: "bg-destructive text-white", past_due: "bg-amber-500 text-white" };

function Metric({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-card rounded-2xl border p-5">
      <div className="flex items-center gap-2 text-muted-foreground text-sm"><Icon size={16} /> {label}</div>
      <p className="font-head text-3xl font-extrabold mt-2">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

function CreateTenantDialog({ open, onClose, onCreated }) {
  const [f, setF] = useState({ business_name: "", commercial_name: "", slug: "", owner_email: "", owner_name: "", owner_password: "", template: "paninoteca", plan_code: "standard", branding_mode: "standard", primary_color: "#FF7A00", phone: "", address: "" });
  const [busy, setBusy] = useState(false);
  const [slugOk, setSlugOk] = useState(null);
  const set = (k, v) => setF({ ...f, [k]: v });

  const checkSlug = async (s) => {
    set("slug", s);
    if (!s) return setSlugOk(null);
    try { const { data } = await api.get(`/platform/slug-available/${s.toLowerCase()}`); setSlugOk(data.available); } catch {}
  };

  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post("/platform/tenants", f); toast.success("Tenant creato"); onCreated(); onClose(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head">Nuovo tenant</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Ragione sociale</Label><Input value={f.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="t-business" /></div>
            <div><Label>Nome commerciale</Label><Input value={f.commercial_name} onChange={(e) => set("commercial_name", e.target.value)} data-testid="t-commercial" /></div>
          </div>
          <div>
            <Label>Slug (sottodominio)</Label>
            <Input value={f.slug} onChange={(e) => checkSlug(e.target.value)} data-testid="t-slug" placeholder="nomeattivita" />
            {slugOk === true && <p className="text-xs text-[hsl(var(--success))]">Disponibile · {f.slug}.pickfood.it</p>}
            {slugOk === false && <p className="text-xs text-destructive">Slug già in uso</p>}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Label>Email titolare</Label><Input value={f.owner_email} onChange={(e) => set("owner_email", e.target.value)} data-testid="t-owner-email" /></div>
            <div><Label>Password</Label><Input value={f.owner_password} onChange={(e) => set("owner_password", e.target.value)} data-testid="t-owner-pass" /></div>
          </div>
          <div><Label>Nome titolare</Label><Input value={f.owner_name} onChange={(e) => set("owner_name", e.target.value)} data-testid="t-owner-name" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Template</Label>
              <Select value={f.template} onValueChange={(v) => set("template", v)}><SelectTrigger data-testid="t-template"><SelectValue /></SelectTrigger>
                <SelectContent>{["paninoteca", "pizzeria", "ristorante", "bar", "vuoto"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Piano</Label>
              <Select value={f.plan_code} onValueChange={(v) => set("plan_code", v)}><SelectTrigger data-testid="t-plan"><SelectValue /></SelectTrigger>
                <SelectContent>{["standard", "custom", "white_label"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Colore</Label><Input type="color" value={f.primary_color} onChange={(e) => set("primary_color", e.target.value)} className="h-10 p-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Telefono</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div><Label>Indirizzo</Label><Input value={f.address} onChange={(e) => set("address", e.target.value)} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button className="brand-bg hover:opacity-90" onClick={submit} disabled={busy} data-testid="t-create-submit">Crea & pubblica</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const PROX_STATUS = { approved: "Approvata", pending: "In revisione", rejected: "Rifiutata", suspended: "Sospesa" };

function ProximityAdmin() {
  const [config, setConfig] = useState(null);
  const [camps, setCamps] = useState([]);
  const load = useCallback(async () => {
    try {
      const [cfg, c] = await Promise.all([
        api.get("/platform/proximity/config"),
        api.get("/platform/proximity/campaigns"),
      ]);
      setConfig(cfg.data); setCamps(c.data);
    } catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveConfig = async (patch) => {
    try { const { data } = await api.post("/platform/proximity/config", patch); setConfig(data); toast.success("Config aggiornata"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setStatus = async (id, status) => {
    try { await api.post(`/platform/proximity/campaigns/${id}/status?status=${status}`); toast.success("Aggiornato"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!config) return null;
  return (
    <div className="mt-10" data-testid="admin-proximity">
      <h2 className="font-head text-xl font-bold flex items-center gap-2 mb-3"><MapPin size={20} className="text-[#FFC400]" /> Offerte di prossimità</h2>
      <div className="bg-card rounded-2xl border p-5 mb-5 grid sm:grid-cols-4 gap-4">
        <div className="flex items-center justify-between sm:col-span-2">
          <div><p className="font-semibold text-sm">Modulo attivo</p><p className="text-xs text-muted-foreground">Mostra offerte a fine ordine</p></div>
          <Switch checked={!!config.enabled} onCheckedChange={(v) => saveConfig({ enabled: v })} data-testid="prox-cfg-enabled" />
        </div>
        <div className="flex items-center justify-between sm:col-span-2">
          <div><p className="font-semibold text-sm">Auto-approvazione</p><p className="text-xs text-muted-foreground">Campagne attive senza revisione</p></div>
          <Switch checked={!!config.auto_approve} onCheckedChange={(v) => saveConfig({ auto_approve: v })} data-testid="prox-cfg-autoapprove" />
        </div>
        <div><Label className="text-xs">Raggio max (km)</Label><Input type="number" step="0.5" defaultValue={config.max_radius_km} onBlur={(e) => saveConfig({ max_radius_km: parseFloat(e.target.value) })} data-testid="prox-cfg-maxradius" /></div>
        <div><Label className="text-xs">Raggio default (km)</Label><Input type="number" step="0.5" defaultValue={config.default_radius_km} onBlur={(e) => saveConfig({ default_radius_km: parseFloat(e.target.value) })} /></div>
      </div>

      <div className="bg-card rounded-2xl border overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Offerta</TableHead><TableHead>Locale</TableHead><TableHead>Raggio</TableHead>
            <TableHead>Stato</TableHead><TableHead>Statistiche</TableHead><TableHead className="text-right">Azioni</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {camps.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Nessuna campagna.</TableCell></TableRow>}
            {camps.map((c) => (
              <TableRow key={c.id} data-testid={`admin-prox-row-${c.id}`}>
                <TableCell><div className="font-semibold">{c.headline}</div><div className="text-xs text-muted-foreground">{c.coupon_code}</div></TableCell>
                <TableCell><div className="text-sm">{c.store_name}</div><div className="text-xs text-muted-foreground">{c.brand}</div></TableCell>
                <TableCell>{c.radius_km} km</TableCell>
                <TableCell><Badge variant="secondary">{PROX_STATUS[c.status] || c.status}</Badge></TableCell>
                <TableCell className="text-xs text-muted-foreground">{c.stats?.views || 0}v · {c.stats?.clicks || 0}c · {c.stats?.conversions || 0}★</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {c.status !== "approved" && <Button size="icon" variant="ghost" title="Approva" data-testid={`admin-prox-approve-${c.id}`} onClick={() => setStatus(c.id, "approved")}><Check size={16} className="text-[hsl(var(--success))]" /></Button>}
                  {c.status !== "suspended" && <Button size="icon" variant="ghost" title="Sospendi" data-testid={`admin-prox-suspend-${c.id}`} onClick={() => setStatus(c.id, "suspended")}><PauseCircle size={16} className="text-amber-500" /></Button>}
                  {c.status !== "rejected" && <Button size="icon" variant="ghost" title="Rifiuta" data-testid={`admin-prox-reject-${c.id}`} onClick={() => setStatus(c.id, "rejected")}><Ban size={16} className="text-destructive" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function OnboardingReport() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/platform/onboarding").then((r) => setRows(r.data)).catch(() => {}); }, []);
  const StepDot = ({ ok, label }) => (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${ok ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" : "bg-muted text-muted-foreground"}`}>
      {ok ? <Check size={12} /> : <X size={12} />}{label}
    </span>
  );
  return (
    <div className="mt-8" data-testid="onboarding-report">
      <h2 className="font-head text-xl font-bold mb-3">Onboarding esercenti</h2>
      <div className="bg-card rounded-2xl border overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Attività</TableHead><TableHead>Titolare</TableHead><TableHead>Registrato</TableHead>
            <TableHead>Progresso</TableHead><TableHead>Passi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.tenant_id} data-testid={`onboarding-row-${r.slug || r.tenant_id}`}>
                <TableCell><div className="font-semibold">{r.business_name}</div><div className="text-xs text-muted-foreground">{r.slug ? `${r.slug}.pickfood.it` : "— nessuno slug"}{r.vat_number ? ` · P.IVA ${r.vat_number}` : ""}</div></TableCell>
                <TableCell><div className="text-sm">{r.owner_name || "—"}</div><div className="text-xs text-muted-foreground flex items-center gap-1">{r.owner_email}{r.email_verified ? <Check size={12} className="text-[hsl(var(--success))]" /> : null}</div></TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleDateString("it-IT") : "—"}</TableCell>
                <TableCell><Badge className={r.done === r.total_steps ? "bg-[hsl(var(--success))] text-white" : "bg-amber-500 text-white"}>{r.done}/{r.total_steps}</Badge></TableCell>
                <TableCell><div className="flex flex-wrap gap-1"><StepDot ok={r.steps.logo} label="Logo" /><StepDot ok={r.steps.menu} label="Menu" /><StepDot ok={r.steps.payments} label="Pagamenti" /></div></TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Nessun esercente ancora.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function AdminConsole() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [dup, setDup] = useState(null);

  const load = useCallback(async () => {
    const [m, t] = await Promise.all([api.get("/platform/metrics"), api.get(`/platform/tenants?q=${q}`)]);
    setMetrics(m.data); setTenants(t.data);
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => { try { await api.post(`/platform/tenants/${id}/status?status=${status}`); toast.success("Aggiornato"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const impersonate = async (id, slug) => {
    // Hard navigation so the AuthProvider re-bootstraps from /auth/me with the new (owner) cookies,
    // avoiding a state race with the /manage role guard while still mounted on /admin.
    try { const { data } = await api.post(`/platform/tenants/${id}/impersonate`); toast.success(`Sessione assistenza · ${data.impersonated}`); window.location.assign("/manage"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--secondary))]">
      <header className="bg-card border-b sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2"><BrandLogo size={30} /><Badge variant="secondary" className="ml-1">Console centrale</Badge></div>
          <Button variant="ghost" onClick={() => { logout(); nav("/login"); }}><LogOut size={18} className="mr-1" />Esci</Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Metric icon={Building2} label="Tenant" value={metrics?.tenants_total ?? "-"} sub={`${metrics?.active ?? 0} attivi · ${metrics?.trialing ?? 0} trial`} />
          <Metric icon={TrendingUp} label="MRR" value={fmt.eur(metrics?.mrr ?? 0)} sub={`ARR ${fmt.eur(metrics?.arr ?? 0)}`} />
          <Metric icon={ShoppingBag} label="Ordini totali" value={metrics?.orders_total ?? "-"} />
          <Metric icon={Users} label="Insoluti" value={metrics?.past_due ?? 0} />
        </div>

        <div className="flex items-center justify-between mt-8 mb-3">
          <h2 className="font-head text-xl font-bold">Tenant</h2>
          <div className="flex gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca..." className="w-48 h-10" data-testid="tenant-search" />
            <Button className="brand-bg hover:opacity-90 rounded-full" onClick={() => setCreateOpen(true)} data-testid="new-tenant-btn"><Plus size={16} className="mr-1" />Nuovo tenant</Button>
          </div>
        </div>

        <div className="bg-card rounded-2xl border overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Attività</TableHead><TableHead>Piano</TableHead><TableHead>Stato</TableHead>
              <TableHead>Sedi</TableHead><TableHead>Ordini</TableHead><TableHead className="text-right">Azioni</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {tenants.map((t) => (
                <TableRow key={t.id} data-testid={`tenant-row-${t.slug}`}>
                  <TableCell><div className="font-semibold">{t.commercial_name}</div><div className="text-xs text-muted-foreground">{t.slug}.pickfood.it</div></TableCell>
                  <TableCell><Badge variant="secondary">{t.subscription?.plan_code}</Badge></TableCell>
                  <TableCell><Badge className={STATUS_COLORS[t.subscription?.status] || ""}>{t.subscription?.status || t.status}</Badge></TableCell>
                  <TableCell>{t.stores_count}</TableCell>
                  <TableCell>{t.orders_count}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" title="Storefront" onClick={() => window.open(`/t/${t.slug}`, "_blank")}><ExternalLink size={16} /></Button>
                    <Button size="icon" variant="ghost" title="Assistenza (impersona)" data-testid={`impersonate-${t.slug}`} onClick={() => impersonate(t.id, t.slug)}><UserCog size={16} /></Button>
                    <Button size="icon" variant="ghost" title="Duplica config" onClick={() => setDup(t)}><Copy size={16} /></Button>
                    {t.status === "suspended"
                      ? <Button size="icon" variant="ghost" title="Riattiva" onClick={() => setStatus(t.id, "active")}><PlayCircle size={16} className="text-[hsl(var(--success))]" /></Button>
                      : <Button size="icon" variant="ghost" title="Sospendi" data-testid={`suspend-${t.slug}`} onClick={() => setStatus(t.id, "suspended")}><PauseCircle size={16} className="text-destructive" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <ProximityAdmin />
        <OnboardingReport />
      </div>

      <CreateTenantDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} />
      <DuplicateDialog tenant={dup} onClose={() => setDup(null)} onDone={load} />
    </div>
  );
}

function DuplicateDialog({ tenant, onClose, onDone }) {
  const [f, setF] = useState({ new_slug: "", business_name: "", owner_email: "", owner_name: "", owner_password: "" });
  const set = (k, v) => setF({ ...f, [k]: v });
  if (!tenant) return null;
  const submit = async () => {
    try {
      const p = new URLSearchParams(f).toString();
      await api.post(`/platform/tenants/${tenant.id}/duplicate?${p}`);
      toast.success("Configurazione duplicata (senza clienti/ordini/credenziali)"); onDone(); onClose();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Duplica «{tenant.commercial_name}»</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Copia solo tema e struttura menu. Esclude utenti, clienti, ordini, pagamenti, credenziali e log.</p>
        <div className="space-y-3 mt-2">
          <Input placeholder="Nuovo slug" onChange={(e) => set("new_slug", e.target.value)} data-testid="dup-slug" />
          <Input placeholder="Nome attività" onChange={(e) => set("business_name", e.target.value)} />
          <Input placeholder="Email titolare" onChange={(e) => set("owner_email", e.target.value)} />
          <Input placeholder="Nome titolare" onChange={(e) => set("owner_name", e.target.value)} />
          <Input placeholder="Password titolare" onChange={(e) => set("owner_password", e.target.value)} />
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Annulla</Button><Button className="brand-bg" onClick={submit} data-testid="dup-submit">Duplica</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
